import React from 'react';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import DataGrid from '../../Component/DataGrid/DataGrid';
import { TableColumn } from 'react-data-table-component';
import { amountFormat } from '../../Component/helper';
import { ShowAlert } from '../../Component/ShowAlert';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import UserAndPermissionService from '../../Services/UserAndPermissionService';
import { IRateReconciliation, RateReconciliationProps, RateReconciliationState } from './Interface';
import RateReconciliationService from '../../Services/RateReconciliationService';
import { Box } from '@mui/material';
import ReusableDrawer from '../../Component/RightDrawer/ReusableDrawer';
import { fetchRecipientCountryList } from '../../Component/Reusables/Recipient';
import moment from 'moment';
import { getLast30DaysRange, getRequestDataForReportsWithDateAndPage, Texts } from '../../Component/Reusables/Utility';
import { formatLimit } from '../../Component/config';

class RateReconciliation extends React.Component<RateReconciliationProps, RateReconciliationState> {
    static contextType = SyncContext;
    context!: SyncContextType;
    private tableColumns: TableColumn<IRateReconciliation>[] = [
        {
            name: 'Transaction Id',
            selector: row => row.transactionPaymentId,
            sortable: false,
            id: "transactionPaymentId",
            minWidth: "200px",
            wrap: true,
        },
        {
            name: 'Pool Id',
            selector: row => row.poolName,
            sortable: false,
            id: "poolName",
            minWidth: "200px",
            wrap: true,
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
            id: "name",
            minWidth: "250px",
            wrap: true,
        },
        {
            name: 'Sender Email',
            selector: row => row.sender_email,
            sortable: true,
            id: "sender_email",
            minWidth: "200px",
            wrap: true
        },
        {
            name: 'Sender Mobile',
            selector: row => row.sender_mobile,
            sortable: true,
            id: "sender_mobile",
            width: "150px",
        },
        {
            name: 'Recipient Name',
            selector: row => row.recipient_name,
            sortable: true,
            id: "recipient_name",
            minWidth: "150px",
            wrap: true,
        },
        {
            name: 'Total Amount(USD)',
            selector: row => formatLimit(row.totalCost, Texts.langEnUs, 'USD'),
            sortable: true,
            id: "totalCost",
            minWidth: "130px",
            right: true
        },
        {
            name: 'Total Amount',
            selector: row => formatLimit(row.destinationCost, Texts.langEnUs, row.destinationCurrencyCode, row.destinationCurrencySymbol),
            sortable: true,
            id: "destinationCost",
            minWidth: "130px",
            right: true
        },
        {
            name: 'Allocation Status',
            selector: row => row.allocationStatusName,
            sortable: true,
            id: "allocationStatusName",
            center: true,
            minWidth: "150px"
        }
    ]

    constructor(props_: RateReconciliationProps) {
        super(props_);
        let monthRange = getLast30DaysRange();
        this.state = {
            accessToken: '',
            rateReconciliationData: [],
            currentPage: 1,
            currentRowsPerPage: 10,
            totalRows: 0,
            searchText: '',
            selectedFilter: '',
            filterValue: '',
            rateReconciliation: {
                isVisible: true,
            },
            isDrawerOpen: false,
            appliedFiltersCount: 0,
            countryId: "",
            selectedDateRange: {
                startDate: monthRange.startDate,
                endDate: monthRange.endDate,
            },
            csvData: [],
        };
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.rateReconciliation?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const rateReconciliation = permissionResponse.permissions.rateReconciliation

                        this.setState({ rateReconciliation }, () => {
                            this.fetchReportData();
                        });

                    } else {
                        this.fetchReportData();
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }

    public render() {

        return (
            <Box className='body-sub-contianer table-container'>
                <DataGrid
                    title='Placeholder Rate Reconciliation'
                    search='Rate Reconciliation'
                    defaultSearch={this.state.searchText}
                    onSearch={(text) => this.setState({ searchText: text, currentPage: 1 }, this.fetchReportData)}
                    onFilterChange={(selectedFilter, filterValue) =>
                        this.setState({ selectedFilter, filterValue, currentPage: 1 }, this.fetchReportData)
                    }
                    columns={this.tableColumns}
                    data={this.state.rateReconciliationData}
                    onChangePage={(page: number, totalRows: number) => {
                        this.setState({
                            currentPage: page
                        }, () => {
                            this.fetchReportData();
                        })
                    }}
                    onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                        this.setState({
                            currentRowsPerPage: currentRowsPerPage_,
                            currentPage: currentPage_
                        }, () => {
                            this.fetchReportData();
                        })
                    }}
                    paginationDefaultPage={1}
                    pagination
                    paginationTotalRows={this.state.totalRows}
                    exportCsv={true}
                    isDrawerRequired={true}
                    isDrawerOpen={() => this.setState({ isDrawerOpen: true })}
                    appliedFiltersCount={this.state.appliedFiltersCount}
                    selectedCountry={this.state.countryId}
                    csvData={this.state.csvData}
                    csvFileName='FX-Placeholder Reconciliation'
                />
                <ReusableDrawer
                    open={this.state.isDrawerOpen}
                    onClose={() => this.setState({ isDrawerOpen: false })}
                    onApply={this.handleDrawerApply}
                    fetchDropdownOptions={this.fetchDropdownOptions}
                    defaultValue={this.state.countryId}
                    onFiltersCountChange={(count) => this.setState({ appliedFiltersCount: count })}
                    defaultStartDate={this.state.selectedDateRange.startDate}
                    defaultEndDate={this.state.selectedDateRange.endDate}
                    showDateRange={true}
                    showTxnStatusFilter={false}
                />
            </Box>

        )
    }

    fetchDropdownOptions = () => {
        return fetchRecipientCountryList(this.state.accessToken);
    };

    handleDrawerApply = (filters: { selectedId: string; startDate: string | null; endDate: string | null }) => {
        const { selectedId, startDate, endDate } = filters;

        this.setState(
            {
                countryId: selectedId || "",
                selectedDateRange: {
                    startDate: startDate || null,
                    endDate: endDate || null,
                },
                currentPage: 1,
            },
            () => {
                this.fetchReportData();

            }
        );
    };

    public fetchReportData = async () => {
        try {
            RateReconciliationService.getAllRateReconciliationList(this.state.accessToken, getRequestDataForReportsWithDateAndPage(this.state)).then(response => {
                if (response.ok) {
                    response.json().then(resp => {
                        this.setState({
                            rateReconciliationData: resp.data,
                            totalRows: resp.totalRecords
                        })
                    });
                }
                else {
                    throw new Error('Failed to load data');
                }
            });
            this.getCSVData();
        } catch (error) {
            ShowAlert("Error", "Failed to load FX Placeholder rate data", "error");
        }
    };

    public getCSVData() {
        RateReconciliationService.getAllRateReconciliationList(this.state.accessToken, getRequestDataForReportsWithDateAndPage(this.state, true), true).then(response => {
            if (response.ok) {
                response.json().then(resp => {
                    this.setState({
                        csvData: resp.data
                    })
                });
            }
        });
    }

}

export default RateReconciliation;